/**
 * Leaving (WALLIYO-0131): what deactivating does not do.
 *
 * Deactivating was one button, and it still is — it ends every session and
 * stops somebody signing in. What it never touched is what this tab is for: a
 * team whose only lead is walking out, and a grant made to somebody by name
 * with no end date. Both are invisible everywhere else, and the second is how
 * somebody who left last quarter still holds a permission.
 *
 * Each loose end links to the screen that fixes it rather than fixing it here.
 * Handing a team over is a change to that team, revoking a grant is a change
 * to that person's permissions, and both are already audited where they live.
 * Doing them again from here would be a second path to the same write.
 */

import type { GetLeavingResponse, LeavingGrant, LeavingTeam } from "@wallyo/contracts/console/hr";
import type { StaffMemberListItem } from "@wallyo/contracts/console";
import { settingName, t } from "../../i18n";

import { formatDateTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { hrHref } from "./hr-route";

export type LeavingState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: GetLeavingResponse }
  | { status: "failed"; error: unknown };

/**
 * A grant's name as the console says it everywhere else. The raw setting id is
 * kept beside it, because that is what the audit log and Permissions show and
 * somebody moving between the two should not have to match them up by eye.
 */
function GrantRow({ grant }: { grant: LeavingGrant }) {
  const forever = grant.expiresAt === null;
  return (
    <tr>
      <td>
        <strong>{settingName(grant.settingId)}</strong>
      </td>
      <td>{grant.reason ?? <span className="foot">{t("leaving.noReason")}</span>}</td>
      <td>
        {forever ? (
          <span className="pill bad">{t("leaving.grant.forever")}</span>
        ) : (
          <>
            <span className="pill">{t("leaving.grant.until")}</span>
            <div className="foot">{formatDateTime(grant.expiresAt as string)}</div>
          </>
        )}
      </td>
    </tr>
  );
}

function TeamRow({ team }: { team: LeavingTeam }) {
  return (
    <tr>
      <td>
        <strong>{team.name}</strong>
      </td>
      <td>{t("leaving.team.others", { count: String(team.otherMembers) })}</td>
      <td>
        {team.wouldHaveNoLead ? (
          <span className="pill bad">{t("leaving.team.noLead")}</span>
        ) : (
          <span className="pill ok">{t("leaving.team.covered")}</span>
        )}
      </td>
      <td className="num">
        <a className="btn quiet small" href="#teams">
          {t("leaving.team.handOver")}
        </a>
      </td>
    </tr>
  );
}

export function LeavingView(props: {
  people: readonly StaffMemberListItem[] | null;
  who: string | null;
  state: LeavingState;
  onWho: (staffMemberId: string | null) => void;
  onRetry: () => void;
  onDeactivate: (member: StaffMemberListItem) => void;
}) {
  const { people, who, state } = props;
  const chosen = people?.find((each) => each.staffMemberId === who) ?? null;

  return (
    <>
      <form className="hr-search">
        <label className="field">
          <span className="label">{t("leaving.who")}</span>
          <select
            value={who ?? ""}
            onChange={(event) => props.onWho(event.currentTarget.value === "" ? null : event.currentTarget.value)}
          >
            <option value="">{t("leaving.pick")}</option>
            {(people ?? []).map((member) => (
              <option key={member.staffMemberId} value={member.staffMemberId}>
                {member.name}
                {member.status === "deactivated" ? ` · ${t("leaving.alreadyLeft")}` : ""}
              </option>
            ))}
          </select>
          <span className="foot">{t("leaving.whoHelp")}</span>
        </label>
      </form>

      {state.status === "idle" ? <div className="empty">{t("leaving.nobody")}</div> : null}

      {state.status === "loading" ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("hr.loading")}</span>
        </div>
      ) : null}

      {state.status === "failed" ? (
        <div className="audit-error">
          <p className="foot">{t("hr.failed")}</p>
          <ErrorNotice error={state.error} />
          <div className="btns">
            <button className="btn" type="button" onClick={props.onRetry}>
              {t("hr.retry")}
            </button>
          </div>
        </div>
      ) : null}

      {state.status === "ready" ? (
        <>
          {state.data.alreadyLeft ? (
            <div className="banner" role="status">
              {t("leaving.gone", { name: state.data.name })}
            </div>
          ) : null}

          <section className="card">
            <h2>{t("leaving.teams.title")}</h2>
            <p className="foot">{t("leaving.teams.help")}</p>
            {state.data.teamsTheyLead.length === 0 ? (
              <p className="foot">{t("leaving.teams.none")}</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>{t("leaving.teams.col.team")}</th>
                      <th>{t("leaving.teams.col.others")}</th>
                      <th>{t("leaving.teams.col.after")}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {state.data.teamsTheyLead.map((team) => (
                      <TeamRow key={team.staffTeamId} team={team} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card">
            <h2>{t("leaving.grants.title")}</h2>
            <p className="foot">{t("leaving.grants.help")}</p>
            {state.data.directGrants.length === 0 ? (
              <p className="foot">{t("leaving.grants.none")}</p>
            ) : (
              <>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>{t("leaving.grants.col.what")}</th>
                        <th>{t("leaving.grants.col.why")}</th>
                        <th>{t("leaving.grants.col.ends")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.data.directGrants.map((grant) => (
                        <GrantRow key={grant.settingId} grant={grant} />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="btns">
                  <a className="btn quiet" href={`#permissions/employee/${state.data.uuid}`}>
                    {t("leaving.grants.revoke")}
                  </a>
                </div>
              </>
            )}
          </section>

          <section className="card">
            <h2>{t("leaving.last.title")}</h2>
            <p className="foot">{t("leaving.last.help", { sessions: String(state.data.openSessions) })}</p>
            <div className="btns">
              <button
                className="btn"
                type="button"
                disabled={state.data.alreadyLeft || !chosen}
                onClick={() => chosen && props.onDeactivate(chosen)}
              >
                {t("leaving.last.deactivate")}
              </button>
              <a className="btn quiet" href={hrHref({ uuid: state.data.uuid })}>
                {t("leaving.last.seeThem")}
              </a>
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}
